import { BACKEND_URL } from '../../config';
/**
 * Command sub-components — all card-type message components, MessageBubble, Dialogs.
 * Extracted from the monolithic Command.jsx for modularity.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  CheckCircle, CircleNotch, WarningCircle, Shield, Info,
  MagnifyingGlass, ArrowRight, Lightning, FlowArrow, Robot,
  Phone, Hammer, Eye, Wrench, ArrowsClockwise, SkipForward,
  Database, Sparkle, PaperPlaneTilt, Plugs, TreeStructure, Cube,
  X, CaretDown, CaretUp, CaretRight, Check, Clock, Warning, Target, Play,
  Plus, Minus, Bell, Trash, Crown, Stack,
  Microphone, MicrophoneSlash, PaperPlaneRight, CheckSquare,
  EnvelopeSimple, Copy, ArrowSquareOut, Star,
  ChatCircle, ShieldCheck,
  House, Exam, ChartBar, CreditCard, UserCircle, UsersThree, Question, Brain,
  ArrowsLeftRight, Gear, Storefront, NavigationArrow, Lightbulb,
} from '@phosphor-icons/react';
import ReactMarkdown from 'react-markdown';
import { useSpeechToText, isSpeechRecognitionSupported } from '../../hooks/useSpeechToText';
import { APP_SLUG_MAP, getSlug } from '../../utils/composioAuth';
import { getAppLogo } from '../../utils/usedApps';
import {
  MSG, STATUS_ICON_MAP, STATUS_COLOR_MAP,
  CREATION_STEPS, CLONE_PHASES,
  CATEGORY_COLORS, CATEGORY_ICONS,
  EXEC_STATUS_STYLES, QUICK_ACTION_CATALOG,
  CHANNEL_ICONS,
} from './constants';

const ActionChipRow = ({ actions = [], onAction, maxVisible = 3, testIdPrefix = 'action' }) => {
  const [expanded, setExpanded] = useState(false);
  if (!actions?.length) return null;

  const visible = expanded ? actions : actions.slice(0, maxVisible);
  const hasMore = actions.length > maxVisible && !expanded;

  return (
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:gap-1.5" data-testid={`${testIdPrefix}-chips`}>
      {visible.map((a, i) => (
        <button
          key={i}
          onClick={() => onAction(a)}
          data-testid={`${testIdPrefix}-chip-${i}`}
          className={`w-full md:w-auto px-4 py-2.5 md:px-3 md:py-1.5 rounded-xl md:rounded-full text-sm md:text-xs font-medium border transition-all active:scale-[0.98] ${
            a.style === 'primary'
              ? 'border-[#00E5C0] text-[#00E5C0] hover:bg-[#00E5C0] hover:text-[#0B1220]'
              : a.style === 'danger'
              ? 'border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white'
              : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)]'
          }`}
        >{a.label}</button>
      ))}
      {hasMore && (
        <button
          onClick={() => setExpanded(true)}
          data-testid={`${testIdPrefix}-show-more`}
          className="w-full md:w-auto px-3 py-2 md:py-1.5 rounded-xl md:rounded-full text-xs md:text-[10px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)] transition-all"
        >+{actions.length - maxVisible} more</button>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// PUSH-TO-TALK — CommandBarMic (retained from original)
// ═══════════════════════════════════════════════════════════════

const CommandBarMic = ({ onTranscript, isListening, setIsListening, onMicActivated }) => {
  const handleResult = useCallback((transcript) => {
    if (transcript && transcript.trim()) onTranscript(transcript.trim());
  }, [onTranscript]);

  const handleError = useCallback((message) => {
    console.warn('[CommandBarMic] Error:', message);
  }, []);

  const {
    isListening: sttListening,
    isProcessing,
    isPermissionDenied,
    startListening,
    stopListening,
  } = useSpeechToText({
    language: 'en-US',
    continuous: false,
    interimResults: true,
    onResult: handleResult,
    onError: handleError
  });

  const browserSupported = isSpeechRecognitionSupported();

  useEffect(() => {
    setIsListening(sttListening);
  }, [sttListening, setIsListening]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!browserSupported) return;
    onMicActivated?.(); // Stop TTS when mic activated
    startListening();
  }, [browserSupported, startListening, onMicActivated]);

  const handleMouseUp = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (sttListening) stopListening();
  }, [sttListening, stopListening]);

  const handleMouseLeave = useCallback(() => {
    if (sttListening) stopListening();
  }, [sttListening, stopListening]);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!browserSupported) return;
    onMicActivated?.(); // Stop TTS when mic activated
    startListening();
  }, [browserSupported, startListening, onMicActivated]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (sttListening) stopListening();
  }, [sttListening, stopListening]);

  if (!browserSupported) {
    return (
      <button type="button" disabled className="p-3 md:p-2 rounded-lg text-[var(--text-muted)] cursor-not-allowed opacity-40" title="Voice not supported">
        <Microphone size={20} className="md:w-[18px] md:h-[18px]" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      disabled={isProcessing}
      className={`p-3 md:p-2 rounded-xl md:rounded-lg transition-all ${
        sttListening
          ? 'bg-[#EF4444] text-white scale-110 shadow-lg shadow-red-500/25'
          : isProcessing
          ? 'bg-[#F59E0B] text-white'
          : isPermissionDenied
          ? 'text-[#EF4444] opacity-50'
          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)]'
      }`}
      title={sttListening ? 'Release to send' : isPermissionDenied ? 'Mic blocked' : 'Hold to speak'}
      data-testid="command-mic-btn"
    >
      {isProcessing ? (
        <CircleNotch size={20} className="animate-spin md:w-[18px] md:h-[18px]" />
      ) : sttListening ? (
        <MicrophoneSlash size={20} className="md:w-[18px] md:h-[18px]" />
      ) : (
        <Microphone size={20} className="md:w-[18px] md:h-[18px]" />
      )}
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════
// EXECUTION PROGRESS — Shows step-by-step execution in thread
// ═══════════════════════════════════════════════════════════════

const ExecutionStep = ({ step, status, label }) => (
  <div className="flex items-center gap-2.5 py-1" data-testid={`exec-step-${step}`}>
    {status === 'active' ? (
      <CircleNotch size={13} className="animate-spin text-[var(--brand-primary)]" />
    ) : status === 'complete' ? (
      <CheckCircle size={13} weight="fill" className="text-[#10B981]" />
    ) : (
      <div className="w-3.5 h-3.5 rounded-full border border-[var(--border-default)]" />
    )}
    <span className={`text-xs ${status === 'active' ? 'text-[var(--brand-primary)] font-medium' : status === 'complete' ? 'text-[#10B981]' : 'text-[var(--text-muted)]'}`}>
      {label}
    </span>
  </div>
);

const ExecutionMessage = ({ message, onAction }) => {
  const { steps = [], execStatus, execResult } = message;

  return (
    <div className="flex gap-3" data-testid="msg-execution">
      <div className="w-7 h-7 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0 mt-0.5">
        {execStatus === 'failed' ? (
          <WarningCircle size={14} weight="fill" className="text-[#F87171]" />
        ) : execStatus === 'success' ? (
          <CheckCircle size={14} weight="fill" className="text-[#10B981]" />
        ) : (
          <Lightning size={14} weight="fill" className="text-[var(--brand-primary)]" />
        )}
      </div>
      <div className="flex-1 space-y-1">
        {/* Step progression */}
        <div className="space-y-0.5">
          {steps.map((s, i) => (
            <ExecutionStep key={i} step={s.step} status={s.status} label={s.label} />
          ))}
        </div>

        {/* Completion result */}
        {execResult && execStatus === 'success' && (
          <div className="mt-2 p-3 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)]">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={13} weight="fill" className="text-[#10B981]" />
              <span className="text-xs font-medium text-[#10B981]">
                Completed{execResult.duration_ms ? ` in ${(execResult.duration_ms / 1000).toFixed(1)}s` : ''}
              </span>
            </div>
            {execResult.output && (
              <p className="text-sm text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap">{execResult.output}</p>
            )}
            {execResult.actions?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {execResult.actions.map((a, i) => (
                  <button key={i} onClick={() => onAction(a)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      a.style === 'primary'
                        ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white'
                        : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]'
                    }`}
                  >{a.label}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error result */}
        {execResult && execStatus === 'failed' && (
          <div className="mt-2 p-3 rounded-lg bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.12)]">
            <div className="flex items-center gap-2 mb-1.5">
              <WarningCircle size={13} weight="fill" className="text-[#F87171]" />
              <span className="text-xs font-medium text-[#F87171]">Execution Failed</span>
            </div>
            <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
              {execResult.error || execResult.message || 'Something went wrong.'}
            </p>
            {execResult.actions?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {execResult.actions.map((a, i) => (
                  <button key={i} onClick={() => onAction(a)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      a.style === 'primary'
                        ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white'
                        : a.style === 'danger'
                        ? 'border-[rgba(239,68,68,0.3)] text-[#F87171] hover:bg-[rgba(239,68,68,0.1)]'
                        : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]'
                    }`}
                  >{a.label}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ORCHESTRATION MESSAGE — Step-by-step pipeline execution display
// ═══════════════════════════════════════════════════════════════

const OrchestrationStep = ({ step, totalSteps }) => {
  const statusStyles = {
    pending: 'border-[var(--border-default)] text-[var(--text-muted)]',
    running: 'border-[var(--brand-primary)] text-[var(--brand-primary)] bg-[rgba(99,102,241,0.06)]',
    completed: 'border-[#10B981] text-[#10B981] bg-[rgba(16,185,129,0.06)]',
    failed: 'border-[#F87171] text-[#F87171] bg-[rgba(239,68,68,0.06)]',
    skipped: 'border-[var(--border-subtle)] text-[var(--text-muted)] opacity-50',
  };

  return (
    <div className={`p-3 rounded-lg border transition-all duration-300 ${statusStyles[step.status] || statusStyles.pending}`} data-testid={`orch-step-${step.index}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {step.status === 'running' ? (
            <CircleNotch size={14} className="animate-spin text-[var(--brand-primary)]" />
          ) : step.status === 'completed' ? (
            <CheckCircle size={14} weight="fill" className="text-[#10B981]" />
          ) : step.status === 'failed' ? (
            <WarningCircle size={14} weight="fill" className="text-[#F87171]" />
          ) : step.status === 'skipped' ? (
            <SkipForward size={14} className="text-[var(--text-muted)]" />
          ) : (
            <div className="w-3.5 h-3.5 rounded-full border-2 border-current" />
          )}
          <div>
            <span className="text-xs font-medium">Step {step.index + 1}: {step.agent_name}</span>
            {step.status === 'running' && step.message && (
              <p className="text-[11px] mt-0.5 opacity-70">{step.message}</p>
            )}
          </div>
        </div>
        {step.duration_ms != null && (
          <span className="text-[10px] opacity-60">{(step.duration_ms / 1000).toFixed(1)}s</span>
        )}
      </div>

      {/* Input preview — shown when step receives piped data */}
      {step.status === 'running' && step.input_preview && (
        <div className="mt-2 pl-6">
          <p className="text-[10px] uppercase tracking-wider opacity-50 mb-0.5">Input received</p>
          <p className="text-xs opacity-70 line-clamp-2">{step.input_preview}</p>
        </div>
      )}

      {/* Output preview — shown on completion */}
      {step.status === 'completed' && step.output_preview && (
        <div className="mt-2 pl-6">
          <p className="text-[10px] uppercase tracking-wider opacity-50 mb-0.5">Output</p>
          <p className="text-xs opacity-80 line-clamp-3 whitespace-pre-wrap">{step.output_preview}</p>
        </div>
      )}

      {/* Error — shown on failure */}
      {step.status === 'failed' && step.error && (
        <div className="mt-2 pl-6">
          <p className="text-xs text-[#F87171] opacity-90">{step.error}</p>
        </div>
      )}

      {/* Pipe arrow to next step */}
      {step.index < totalSteps - 1 && step.status !== 'skipped' && (
        <div className="flex justify-center mt-2 -mb-1">
          <ArrowRight size={12} className="opacity-30" />
        </div>
      )}
    </div>
  );
};

const OrchestrationMessage = ({ message, onAction }) => {
  const { orchSteps = [], orchStatus, orchResult, orchPlan } = message;
  const isRunning = orchStatus === 'running';
  const isComplete = orchStatus === 'completed';
  const isFailed = orchStatus === 'failed';

  const completedCount = orchSteps.filter(s => s.status === 'completed').length;
  const totalSteps = orchSteps.length || orchPlan?.step_count || 0;

  return (
    <div className="flex gap-3" data-testid="msg-orchestration">
      <div className="w-7 h-7 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0 mt-0.5">
        {isFailed ? (
          <WarningCircle size={14} weight="fill" className="text-[#F87171]" />
        ) : isComplete ? (
          <CheckCircle size={14} weight="fill" className="text-[#10B981]" />
        ) : (
          <FlowArrow size={14} weight="fill" className="text-[var(--brand-primary)]" />
        )}
      </div>
      <div className="flex-1 space-y-2 min-w-0">
        {/* Header with progress indicator */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
            {isRunning ? 'Pipeline Running' : isComplete ? 'Pipeline Complete' : isFailed ? 'Pipeline Failed' : 'Pipeline'}
          </span>
          {totalSteps > 0 && (
            <span className="text-[10px] text-[var(--text-muted)]">
              {completedCount}/{totalSteps} steps
            </span>
          )}
        </div>

        {/* Progress bar */}
        {totalSteps > 0 && (
          <div className="w-full h-1 rounded-full bg-[var(--surface-3)] overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${isFailed ? 'bg-[#F87171]' : 'bg-[#10B981]'}`}
              style={{ width: `${(completedCount / totalSteps) * 100}%` }}
            />
          </div>
        )}

        {/* Steps */}
        <div className="space-y-1.5">
          {orchSteps.map((step) => (
            <OrchestrationStep key={step.index} step={step} totalSteps={totalSteps} />
          ))}
        </div>

        {/* Final output on completion */}
        {isComplete && orchResult && (
          <div className="p-3 rounded-lg bg-[var(--surface-2)] border border-[#10B981]/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={13} weight="fill" className="text-[#10B981]" />
              <span className="text-xs font-medium text-[#10B981]">
                All {totalSteps} steps completed{orchResult.total_duration_ms ? ` in ${(orchResult.total_duration_ms / 1000).toFixed(1)}s` : ''}
              </span>
            </div>
            {orchResult.final_output && (
              <p className="text-sm text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap line-clamp-6">
                {orchResult.final_output}
              </p>
            )}
            {orchResult.actions?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {orchResult.actions.map((a, i) => (
                  <button key={i} onClick={() => onAction(a)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      a.style === 'primary'
                        ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white'
                        : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]'
                    }`}
                  >{a.label}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error state with recovery actions */}
        {isFailed && orchResult && (
          <div className="p-3 rounded-lg bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.12)]">
            <div className="flex items-center gap-2 mb-1.5">
              <WarningCircle size={13} weight="fill" className="text-[#F87171]" />
              <span className="text-xs font-medium text-[#F87171]">
                Pipeline failed at step {(orchResult.failed_at_step ?? 0) + 1}
                {orchResult.total_duration_ms ? ` (${(orchResult.total_duration_ms / 1000).toFixed(1)}s elapsed)` : ''}
              </span>
            </div>
            <p className="text-xs text-[var(--text-secondary)] leading-relaxed mb-2">
              {orchResult.error || 'Something went wrong during pipeline execution.'}
            </p>
            {orchResult.actions?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {orchResult.actions.map((a, i) => (
                  <button key={i} onClick={() => onAction(a)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      a.style === 'primary'
                        ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white'
                        : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]'
                    }`}
                  >{a.label}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// CREATION MESSAGE — Agent creation progress display
// ═══════════════════════════════════════════════════════════════

// CREATION_STEPS imported from ./constants

const CreationStepIcon = ({ stepKey, status }) => {
  if (status === 'running') return <CircleNotch size={13} className="animate-spin text-[var(--brand-primary)]" />;
  if (status === 'completed') return <CheckCircle size={13} weight="fill" className="text-[#10B981]" />;
  if (status === 'error') return <WarningCircle size={13} weight="fill" className="text-[#F87171]" />;
  return <div className="w-3 h-3 rounded-full border-2 border-[var(--border-default)]" />;
};

const CreationMessage = ({ message, onAction }) => {
  const { creationPhase, creationData, creationError } = message;
  const isComplete = creationPhase === 'done';
  const isError = creationPhase === 'error';

  // Determine step statuses
  const stepIdx = CREATION_STEPS.findIndex(s => s.key === creationPhase);
  const getStepStatus = (idx) => {
    if (isError && idx === stepIdx) return 'error';
    if (idx < stepIdx || isComplete) return 'completed';
    if (idx === stepIdx) return 'running';
    return 'pending';
  };

  return (
    <div className="flex gap-3" data-testid="msg-creation">
      <div className="w-7 h-7 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0 mt-0.5">
        {isError ? (
          <WarningCircle size={14} weight="fill" className="text-[#F87171]" />
        ) : isComplete ? (
          <CheckCircle size={14} weight="fill" className="text-[#10B981]" />
        ) : (
          <Hammer size={14} weight="fill" className="text-[var(--brand-primary)]" />
        )}
      </div>
      <div className="flex-1 space-y-2.5 min-w-0">
        {/* Header */}
        <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
          {isComplete ? 'Worker Created' : isError ? 'Creation Failed' : 'Creating Worker'}
        </span>

        {/* Progress steps */}
        <div className="space-y-1">
          {CREATION_STEPS.filter((_, i) => i <= Math.max(stepIdx, 0) || isComplete).map((step, i) => {
            const status = getStepStatus(i);
            return (
              <div key={step.key} className={`flex items-center gap-2 py-1 transition-all duration-300 ${
                status === 'running' ? 'opacity-100' : status === 'completed' ? 'opacity-70' : 'opacity-40'
              }`} data-testid={`creation-step-${step.key}`}>
                <CreationStepIcon stepKey={step.key} status={status} />
                <span className="text-xs">{step.label}</span>
                {step.key === 'intent' && status === 'completed' && creationData?.intent?.goal && (
                  <span className="text-[10px] text-[var(--text-muted)] ml-1 truncate max-w-[200px]">
                    — {creationData.intent.goal}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Workflow summary (shown after workflow step) */}
        {creationData?.workflow && (stepIdx >= 2 || isComplete) && (
          <div className="p-2.5 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] space-y-1.5">
            <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1">
              <FlowArrow size={10} /> Workflow Generated
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              {creationData.workflow.nodes?.map((label, i) => (
                <React.Fragment key={i}>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-[var(--surface-3)] text-[var(--text-secondary)]">
                    {label}
                  </span>
                  {i < creationData.workflow.nodes.length - 1 && (
                    <ArrowRight size={8} className="text-[var(--text-muted)] opacity-40" />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="text-[10px] text-[var(--text-muted)]">
              {creationData.workflow.node_count} nodes · {creationData.workflow.edge_count} connections
            </p>
          </div>
        )}

        {/* Agent card (shown on completion) */}
        {isComplete && creationData?.saved && (
          <div className="p-3 rounded-lg bg-[var(--surface-2)] border border-[#10B981]/20">
            <div className="flex items-center gap-2 mb-1.5">
              <CheckCircle size={13} weight="fill" className="text-[#10B981]" />
              <span className="text-xs font-medium text-[#10B981]">Worker Ready</span>
            </div>
            <p className="text-sm font-medium text-[var(--text-primary)]">{creationData.saved.agent_name}</p>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5 leading-relaxed">
              {creationData.saved.agent_description}
            </p>
            {creationData.done?.workflow_summary && (
              <div className="flex gap-3 mt-2 text-[10px] text-[var(--text-muted)]">
                <span>In: {creationData.done.workflow_summary.inputs?.join(', ')}</span>
                <span>Actions: {creationData.done.workflow_summary.actions?.join(', ')}</span>
                <span>Out: {creationData.done.workflow_summary.outputs?.join(', ')}</span>
              </div>
            )}
          </div>
        )}

        {/* Error state */}
        {isError && creationError && (
          <div className="p-2.5 rounded-lg bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.12)]">
            <p className="text-xs text-[#F87171]">{creationError}</p>
          </div>
        )}

        {/* Action chips (shown on completion or error) */}
        {(isComplete || isError) && creationData?.done?.actions?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {creationData.done.actions.map((a, i) => (
              <button key={i} onClick={() => onAction(a)}
                data-testid={`creation-action-${a.type}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  a.style === 'primary'
                    ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white'
                    : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]'
                }`}
              >{a.label}</button>
            ))}
          </div>
        )}

        {/* Credits info */}
        {creationData?.start?.credits_deducted && (
          <p className="text-[10px] text-[var(--text-muted)]">
            {creationData.start.credits_deducted} credits used · Balance: {creationData.start.balance_after}
          </p>
        )}
      </div>
    </div>
  );
};





// ═══════════════════════════════════════════════════════════════
// WORKFLOW CHANGE CARD — Structured modification display
// ═══════════════════════════════════════════════════════════════

const WorkflowChangeCard = ({ message, onAction }) => {
  const { modPhase, modData, modError } = message;
  const isPlan = modPhase === 'plan';
  const isPreview = modPhase === 'preview';
  const isNoOp = modData?.no_op === true;
  const isApplied = (modPhase === 'applied' || modPhase === 'done') && !isNoOp;
  const isError = modPhase === 'error';
  const isUndone = modPhase === 'undone';

  const cs = modData?.change_summary || {};
  const ops = modData?.operations || [];

  return (
    <div className="flex gap-3" data-testid="msg-modification">
      <div className="w-7 h-7 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0 mt-0.5">
        {isError ? <WarningCircle size={14} weight="fill" className="text-[#F87171]" />
         : isUndone ? <ArrowsClockwise size={14} weight="fill" className="text-[#F59E0B]" />
         : isNoOp ? <Lightbulb size={14} weight="fill" className="text-[#F59E0B]" />
         : isApplied ? <CheckCircle size={14} weight="fill" className="text-[#10B981]" />
         : isPreview ? <Eye size={14} weight="fill" className="text-[#F59E0B]" />
         : <Wrench size={14} weight="fill" className="text-[var(--brand-primary)]" />}
      </div>
      <div className="flex-1 space-y-2 min-w-0">
        {/* Header */}
        <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
          {isUndone ? 'Change Reverted'
            : isNoOp ? 'Pick a next step'
            : isApplied ? 'Workflow Updated'
            : isPreview ? 'Proposed Change'
            : isError ? 'Modification Failed'
            : 'Analyzing Change'}
        </span>

        {/* Target agent display */}
        {modData?.agent_name && (
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]" data-testid="modify-target-display">
            <span className="text-[var(--text-muted)]">Target:</span>
            <span className="font-medium text-[var(--text-primary)]">{modData.agent_name}</span>
            <span className="text-[var(--text-muted)]">·</span>
            <span className="text-[var(--text-muted)]">Worker</span>
          </div>
        )}

        {/* Plan summary */}
        {modData?.plan_summary && (
          <p className="text-sm text-[var(--text-primary)] leading-relaxed">{modData.plan_summary}</p>
        )}

        {/* Operations log */}
        {ops.length > 0 && (
          <div className="space-y-1">
            {ops.map((op, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${
                  op.op === 'add_node' ? 'bg-[rgba(16,185,129,0.1)] text-[#10B981]'
                  : op.op === 'remove_node' ? 'bg-[rgba(239,68,68,0.1)] text-[#F87171]'
                  : op.op === 'replace_node' ? 'bg-[rgba(245,158,11,0.1)] text-[#F59E0B]'
                  : 'bg-[var(--surface-3)] text-[var(--text-muted)]'
                }`}>
                  {op.op === 'add_node' ? '+add' : op.op === 'remove_node' ? '-remove' : op.op === 'replace_node' ? '~replace' : op.op}
                </span>
                <span className="text-[var(--text-secondary)]">
                  {op.op === 'add_node' && `${op.label} ${op.position ? `(${op.position})` : ''}`}
                  {op.op === 'remove_node' && op.label}
                  {op.op === 'replace_node' && `${op.old_label} → ${op.new_label}`}
                  {op.op === 'rename_node' && `${op.old_label} → ${op.new_label}`}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Change summary card */}
        {(isApplied || isPreview) && (cs.added?.length > 0 || cs.removed?.length > 0 || cs.modified?.length > 0) && (
          <div className={`p-2.5 rounded-lg border space-y-1.5 ${
            isPreview ? 'bg-[rgba(245,158,11,0.04)] border-[rgba(245,158,11,0.15)]' : 'bg-[var(--surface-2)] border-[#10B981]/20'
          }`}>
            {cs.added?.map((n, i) => (
              <div key={`a-${i}`} className="flex items-center gap-1.5 text-xs">
                <span className="text-[#10B981] font-medium">+</span>
                <span className="text-[var(--text-primary)]">Added: {n.label}</span>
              </div>
            ))}
            {cs.removed?.map((n, i) => (
              <div key={`r-${i}`} className="flex items-center gap-1.5 text-xs">
                <span className="text-[#F87171] font-medium">-</span>
                <span className="text-[var(--text-primary)]">Removed: {n.label}</span>
              </div>
            ))}
            {cs.modified?.map((n, i) => (
              <div key={`m-${i}`} className="flex items-center gap-1.5 text-xs">
                <span className="text-[#F59E0B] font-medium">~</span>
                <span className="text-[var(--text-primary)]">Modified: {n.label}</span>
              </div>
            ))}
            <div className="flex gap-3 text-[10px] text-[var(--text-muted)] pt-0.5">
              <span>Impacted: {cs.impacted_count || 0} nodes</span>
              {isApplied && <span className="text-[#10B981]">Saved</span>}
              {isPreview && <span className="text-[#F59E0B]">Awaiting confirmation</span>}
            </div>
          </div>
        )}

        {/* Preview confirmation buttons */}
        {isPreview && (
          <div className="flex gap-2">
            <button
              onClick={() => onAction({ type: 'confirm_modify', payload: { diff_id: modData?.diff_id } })}
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--brand-primary)] text-white hover:opacity-90 transition-all"
              data-testid="confirm-modify-btn"
            >
              Apply Change
            </button>
            <button
              onClick={() => onAction({ type: 'cancel_modify', payload: { diff_id: modData?.diff_id } })}
              className="px-3 py-1.5 rounded-full text-xs font-medium border border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-strong)] transition-all"
              data-testid="cancel-modify-btn"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Undo confirmation */}
        {isUndone && modData?.message && (
          <p className="text-xs text-[var(--text-secondary)]">{modData.message}</p>
        )}

        {/* Error */}
        {isError && modError && (
          <div className="p-2.5 rounded-lg bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.12)] space-y-2">
            <p className="text-xs text-[#F87171]">{modError}</p>
            {modData?.recovery_actions?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {modData.recovery_actions.map((a, i) => (
                  <button key={i} onClick={() => onAction(a)}
                    data-testid={`mod-recovery-${a.type}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      a.style === 'primary'
                        ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white'
                        : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]'
                    }`}
                  >{a.label}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action chips */}
        {(isApplied || isNoOp) && modData?.actions?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {modData.actions.map((a, i) => (
              <button key={i} onClick={() => onAction(a)}
                data-testid={`mod-action-${a.type}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  a.style === 'primary'
                    ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white'
                    : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]'
                }`}
              >{a.label}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// COMPACT RESULT CARD — Lightweight inline results
// ═══════════════════════════════════════════════════════════════

const CompactResultCard = ({ agent, onRun, onViewDetails, isBasicTier }) => {
  // Support both old agent format and new structured result format
  const agentId = agent.agent_id || agent.id;
  const name = agent.name || 'Agent';
  const description = agent.short_description || agent.description || '';
  const capProfile = agent.capability_profile || agent.meta?.capability_profile || 'standard';
  const isVoice = capProfile === 'live_voice_s2s_agent';

  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-1)] hover:border-[var(--border-strong)] transition-colors group cursor-pointer"
      onClick={() => onViewDetails(agent)}
      data-testid={`result-card-${agentId}`}
    >
      <div className="w-8 h-8 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0">
        {isVoice ? <Phone size={14} className="text-[var(--brand-secondary)]" /> : <Robot size={14} className="text-[var(--brand-primary)]" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)] truncate">{name}</p>
        <p className="text-xs text-[var(--text-muted)] truncate">{description}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        {!isVoice && (
          <button
            onClick={(e) => { e.stopPropagation(); onRun(agent); }}
            className="px-2.5 py-1 rounded-md text-xs font-medium bg-[var(--brand-primary)] text-white hover:opacity-90 transition-opacity"
            data-testid={`result-run-${agentId}`}
          >
            Run
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onViewDetails(agent); }}
          className="px-2.5 py-1 rounded-md text-xs font-medium text-[var(--text-secondary)] bg-[var(--surface-3)] hover:bg-[var(--surface-4)] transition-colors"
          data-testid={`result-details-${agentId}`}
        >
          Details
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MESSAGE BUBBLE — Renders each message by type
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// QUICK ACTION CHIPS — Context-aware edit mode accelerators
// ═══════════════════════════════════════════════════════════════

// QUICK_ACTION_CATALOG imported from ./constants

const QuickActionChips = ({ workflowState, onAction }) => {
  const suggestions = useMemo(() => {
    if (!workflowState) return QUICK_ACTION_CATALOG.slice(0, 4);
    // Filter out actions that already exist in the workflow
    const existingTypes = new Set(
      (workflowState.nodes || []).map(n => (n.type || '').toLowerCase())
    );
    const existingLabels = new Set(
      (workflowState.nodes || []).map(n => (n.data?.label || '').toLowerCase())
    );

    return QUICK_ACTION_CATALOG.filter(qa => {
      // Skip if a node with matching keyword already exists
      return !qa.keywords.some(kw =>
        existingTypes.has(kw) || [...existingLabels].some(l => l.includes(kw))
      );
    }).slice(0, 5);
  }, [workflowState]);

  if (suggestions.length === 0) return null;

  return (
    <div className="pl-5 pr-4 pb-1.5 shrink-0 max-w-[520px] ml-0" data-testid="quick-action-chips">
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map(qa => {
          const Icon = qa.icon;
          return (
            <button
              key={qa.id}
              onClick={() => onAction(qa.label)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-1)] text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--brand-primary)] hover:bg-[rgba(0,229,192,0.06)] transition-all"
              data-testid={`quick-action-${qa.id}`}
            >
              <Icon size={11} weight="duotone" />
              {qa.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// RUN EXPLANATION CARD — Structured run explanation display
// ═══════════════════════════════════════════════════════════════

// STATUS_ICON_MAP and STATUS_COLOR_MAP imported from ./constants


// ═══════════════════════════════════════════════════════════════
// CLONE / TEMPLATE MESSAGE — Shows clone progress + result
// ═══════════════════════════════════════════════════════════════

// CLONE_PHASES imported from ./constants

const CloneTemplateMessage = ({ message, onAction }) => {
  const { clonePhase = 'parsing', cloneData = {}, cloneError } = message;
  const phase = CLONE_PHASES[clonePhase] || CLONE_PHASES.parsing;
  const PhaseIcon = phase.icon;

  return (
    <div className="flex gap-3" data-testid="msg-clone-template">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `color-mix(in srgb, ${phase.color} 12%, transparent)` }}>
        <PhaseIcon size={14} weight="fill" className={phase.spin ? 'animate-spin' : ''} style={{ color: phase.color }} />
      </div>
      <div className="flex-1 space-y-2 min-w-0">
        <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">{phase.label}</span>

        {/* Source info */}
        {cloneData.source_name && (
          <div className="p-2.5 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] space-y-1" data-testid="clone-source-info">
            <div className="flex items-start gap-2 text-xs">
              <span className="text-[var(--text-muted)] shrink-0 min-w-[70px]">Source</span>
              <span className="text-[var(--text-primary)] font-medium">{cloneData.source_name}</span>
            </div>
            {cloneData.node_count != null && (
              <div className="flex items-start gap-2 text-xs">
                <span className="text-[var(--text-muted)] shrink-0 min-w-[70px]">Workflow</span>
                <span className="text-[var(--text-primary)]">{cloneData.node_count} nodes, {cloneData.edge_count || 0} edges</span>
              </div>
            )}
            {cloneData.node_labels?.length > 0 && (
              <div className="flex items-start gap-2 text-xs">
                <span className="text-[var(--text-muted)] shrink-0 min-w-[70px]">Steps</span>
                <span className="text-[var(--text-secondary)]">{cloneData.node_labels.join(' \u2192 ')}</span>
              </div>
            )}
          </div>
        )}

        {/* Modification summary */}
        {cloneData.modification_description && clonePhase !== 'parsing' && (
          <p className="text-sm text-[var(--text-secondary)]">
            <span className="font-medium text-[var(--text-primary)]">Modifications:</span> {cloneData.modification_description}
          </p>
        )}

        {/* Change summary */}
        {cloneData.change_summary && (cloneData.change_summary.added?.length > 0 || cloneData.change_summary.removed?.length > 0 || cloneData.change_summary.modified?.length > 0) && (
          <div className="space-y-1" data-testid="clone-change-summary">
            {cloneData.change_summary.added?.map((a, i) => (
              <div key={`a${i}`} className="flex items-center gap-1.5 text-xs text-[#10B981]">
                <Plus size={10} className="shrink-0" /> <span>Added: {a}</span>
              </div>
            ))}
            {cloneData.change_summary.removed?.map((r, i) => (
              <div key={`r${i}`} className="flex items-center gap-1.5 text-xs text-[#F87171]">
                <Minus size={10} className="shrink-0" /> <span>Removed: {r}</span>
              </div>
            ))}
            {cloneData.change_summary.modified?.map((m, i) => (
              <div key={`m${i}`} className="flex items-center gap-1.5 text-xs text-[var(--brand-primary)]">
                <ArrowRight size={10} className="shrink-0" /> <span>Modified: {m}</span>
              </div>
            ))}
          </div>
        )}

        {/* New agent info */}
        {cloneData.agent_name && clonePhase === 'done' && (
          <div className="p-2.5 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] space-y-1" data-testid="clone-result-info">
            <div className="flex items-start gap-2 text-xs">
              <span className="text-[var(--text-muted)] shrink-0 min-w-[70px]">New Worker</span>
              <span className="text-[var(--text-primary)] font-semibold">{cloneData.agent_name}</span>
            </div>
            {cloneData.agent_description && (
              <div className="flex items-start gap-2 text-xs">
                <span className="text-[var(--text-muted)] shrink-0 min-w-[70px]">Description</span>
                <span className="text-[var(--text-secondary)]">{cloneData.agent_description}</span>
              </div>
            )}
            <div className="flex items-start gap-2 text-xs">
              <span className="text-[var(--text-muted)] shrink-0 min-w-[70px]">Cloned from</span>
              <span className="text-[var(--text-secondary)]">{cloneData.cloned_from}</span>
            </div>
            {cloneData.workflow_summary?.nodes?.length > 0 && (
              <div className="flex items-start gap-2 text-xs">
                <span className="text-[var(--text-muted)] shrink-0 min-w-[70px]">Workflow</span>
                <span className="text-[var(--text-secondary)]">{cloneData.workflow_summary.nodes.join(' \u2192 ')}</span>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {cloneError && (
          <div className="p-2 rounded-lg bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.15)]" data-testid="clone-error">
            <p className="text-xs text-[#F87171]">{cloneError}</p>
            {cloneData.available_agents?.length > 0 && (
              <div className="mt-1.5 space-y-0.5">
                <p className="text-[10px] text-[var(--text-muted)] uppercase">Available agents:</p>
                {cloneData.available_agents.map((name, i) => (
                  <button
                    key={i}
                    onClick={() => onAction({ type: 'command', label: name, payload: { value: `clone ${name}` } })}
                    className="text-xs text-[var(--brand-primary)] hover:underline cursor-pointer block"
                    data-testid={`clone-suggestion-${i}`}
                  >{name}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {cloneData.actions?.length > 0 && clonePhase === 'done' && (
          <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid="clone-actions">
            {cloneData.actions.map((a, i) => (
              <button
                key={i}
                onClick={() => onAction(a)}
                data-testid={`clone-action-${i}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  a.style === 'primary'
                    ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white'
                    : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)]'
                }`}
              >{a.label}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const RunExplanationCard = ({ message, onAction }) => {
  const explanation = message.runExplanation || {};
  const { title, status, status_color, summary, key_facts = [], decision_reasons = [], steps = [], actions = [] } = explanation;

  const statusConf = STATUS_ICON_MAP[status] || STATUS_ICON_MAP.unknown;
  const StatusIcon = statusConf.icon;
  const accentColor = STATUS_COLOR_MAP[status_color] || 'var(--text-muted)';

  return (
    <div className="flex gap-3" data-testid="msg-run-explanation">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `color-mix(in srgb, ${accentColor} 12%, transparent)` }}>
        <StatusIcon size={14} weight={statusConf.weight} className={statusConf.spin ? 'animate-spin' : ''} style={{ color: accentColor }} />
      </div>
      <div className="flex-1 space-y-2 min-w-0">
        {/* Title & status badge */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">{title || 'Run Explanation'}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: `color-mix(in srgb, ${accentColor} 10%, transparent)`, color: accentColor }}>
            {(status || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </span>
        </div>

        {/* Summary (supports **bold** markdown) */}
        {summary && (
          <p className="text-sm text-[var(--text-primary)] leading-relaxed" data-testid="explanation-summary">
            {summary.split(/(\*\*.*?\*\*)/).map((part, i) =>
              part.startsWith('**') && part.endsWith('**')
                ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
                : part
            )}
          </p>
        )}

        {/* Key facts grid */}
        {key_facts.length > 0 && (
          <div className="p-2.5 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] space-y-1" data-testid="explanation-facts">
            {key_facts.map((fact, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className="text-[var(--text-muted)] shrink-0 min-w-[80px]">{fact.label}</span>
                <span className="text-[var(--text-primary)] break-words">{fact.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Decision reasons */}
        {decision_reasons.length > 0 && (
          <div className="space-y-1" data-testid="explanation-reasons">
            <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">Decision Reasons</p>
            {decision_reasons.map((reason, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs text-[var(--text-secondary)]">
                <CaretRight size={10} className="shrink-0 mt-0.5 text-[var(--text-muted)]" />
                <span>{reason}</span>
              </div>
            ))}
          </div>
        )}

        {/* Steps (for step-by-step breakdown) */}
        {steps.length > 0 && (
          <div className="space-y-1" data-testid="explanation-steps">
            <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">Execution Steps</p>
            {steps.map((step, i) => {
              const stepStatus = step.status || 'unknown';
              const stepColor = STATUS_COLOR_MAP[stepStatus === 'completed' ? 'green' : stepStatus === 'failed' ? 'red' : 'gray'] || 'var(--text-muted)';
              return (
                <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-[var(--surface-2)]">
                  <span className="text-[var(--text-muted)] w-5 shrink-0 text-center">{step.index}</span>
                  <span className="font-mono text-[10px] px-1 py-0.5 rounded" style={{ background: `color-mix(in srgb, ${stepColor} 10%, transparent)`, color: stepColor }}>
                    {stepStatus}
                  </span>
                  <span className="text-[var(--text-primary)]">{step.type}</span>
                  {step.provider && step.provider !== '—' && <span className="text-[var(--text-muted)]">via {step.provider}</span>}
                  {step.duration && step.duration !== '—' && <span className="text-[var(--text-muted)] ml-auto">{step.duration}</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Action chips */}
        {actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid="explanation-actions">
            {actions.map((a, i) => (
              <button
                key={i}
                onClick={() => onAction(a)}
                data-testid={`explanation-action-${i}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  a.style === 'primary'
                    ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white'
                    : a.style === 'danger'
                    ? 'border-[rgba(239,68,68,0.3)] text-[#F87171] hover:bg-[rgba(239,68,68,0.1)]'
                    : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)]'
                }`}
              >{a.label}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// CAPABILITY PLAN CARD — Structured plan proposal with gap analysis
// ═══════════════════════════════════════════════════════════════

// CATEGORY_COLORS and CATEGORY_ICONS imported from ./constants

const CapabilityPlanCard = ({ message, onAction }) => {
  const plan = message.platformData || {};
  const { goal, available = [], gaps = [], plan_steps = [], coverage_pct = 0, workspace_stats = {} } = plan;
  const actions = message.actions || [];

  const isCoverage = plan.type === 'capability_coverage';

  if (isCoverage) {
    return <CoverageAnalysisCard message={message} onAction={onAction} />;
  }

  return (
    <div className="flex gap-3" data-testid="msg-capability-plan">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 bg-[color-mix(in_srgb,#8B5CF6_12%,transparent)]">
        <Target size={14} weight="fill" style={{ color: '#8B5CF6' }} />
      </div>
      <div className="flex-1 space-y-3 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Capability Plan</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
            style={{
              background: coverage_pct >= 80 ? 'rgba(16,185,129,0.1)' : coverage_pct >= 40 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
              color: coverage_pct >= 80 ? '#10B981' : coverage_pct >= 40 ? '#F59E0B' : '#EF4444',
            }}>
            {coverage_pct}% Coverage
          </span>
        </div>

        {/* Goal */}
        {goal && (
          <p className="text-sm text-[var(--text-primary)] leading-relaxed" data-testid="plan-goal">
            {message.content?.split(/(\*\*.*?\*\*)/).map((part, i) =>
              part.startsWith('**') && part.endsWith('**')
                ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
                : part
            )}
          </p>
        )}

        {/* Workspace Stats */}
        <div className="flex gap-3 text-xs text-[var(--text-muted)]" data-testid="plan-workspace-stats">
          <span>{workspace_stats.agents || 0} agents</span>
          <span>{workspace_stats.apps || 0} apps</span>
          <span>{workspace_stats.external || 0} external</span>
        </div>

        {/* Plan Steps */}
        {plan_steps.length > 0 && (
          <div className="space-y-1" data-testid="plan-steps">
            <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">Plan Steps</p>
            {plan_steps.map((step, i) => {
              const catColor = CATEGORY_COLORS[step.category] || CATEGORY_COLORS.unknown;
              const CatIcon = CATEGORY_ICONS[step.category] || Cube;
              const isAvailable = step.status === 'available';
              return (
                <div key={i} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)]" data-testid={`plan-step-${i}`}>
                  <span className="w-5 h-5 rounded flex items-center justify-center shrink-0"
                    style={{ background: `color-mix(in srgb, ${catColor} 12%, transparent)` }}>
                    <CatIcon size={11} weight="bold" style={{ color: catColor }} />
                  </span>
                  <span className="font-medium text-[var(--text-primary)] flex-1">{step.capability}</span>
                  {isAvailable ? (
                    <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[rgba(16,185,129,0.08)] text-[#10B981]">
                      <CheckCircle size={10} weight="fill" />
                      {step.provider}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[rgba(239,68,68,0.08)] text-[#F87171]">
                      <WarningCircle size={10} weight="fill" />
                      Missing
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Gap Details */}
        {gaps.length > 0 && (
          <div className="space-y-1.5" data-testid="plan-gaps">
            <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">
              Gap Resolution ({gaps.length} {gaps.length === 1 ? 'gap' : 'gaps'})
            </p>
            {gaps.map((gap, i) => (
              <div key={i} className="p-2 rounded-lg bg-[rgba(239,68,68,0.04)] border border-[rgba(239,68,68,0.12)] space-y-1.5" data-testid={`plan-gap-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[var(--text-primary)]">{gap.name}</span>
                  {gap.risk_level && gap.risk_level !== 'safe' && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-[rgba(245,158,11,0.1)] text-[#F59E0B]">{gap.risk_level}</span>
                  )}
                </div>
                {gap.suggestions?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {gap.suggestions.map((s, j) => (
                      <button
                        key={j}
                        onClick={() => onAction({ type: s.action_type, label: s.label, payload: s.payload, style: 'secondary' })}
                        className="px-2 py-1 rounded text-[10px] font-medium border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)] transition-all"
                        data-testid={`gap-${i}-suggestion-${j}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Action chips — includes execute plan */}
        <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid="plan-actions">
          {plan_steps.length > 0 && (
            <button
              onClick={() => onAction({
                type: 'execute_plan',
                label: 'Preview Plan',
                payload: { plan_data: plan, mode: 'preview' },
                style: 'primary',
              })}
              data-testid="preview-plan-btn"
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6] hover:text-white flex items-center gap-1"
            >
              <Eye size={10} weight="bold" /> Preview Plan
            </button>
          )}
          {plan_steps.length > 0 && (
            <button
              onClick={() => onAction({
                type: 'execute_plan',
                label: 'Execute Plan',
                payload: { plan_data: plan, mode: 'staged' },
                style: 'primary',
              })}
              data-testid="execute-plan-btn"
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white flex items-center gap-1"
            >
              <Play size={10} weight="fill" /> Execute All
            </button>
          )}
          {plan_steps.length > 0 && (
            <button
              onClick={() => onAction({
                type: 'execute_plan',
                label: 'Step by Step',
                payload: { plan_data: plan, mode: 'step' },
                style: 'secondary',
              })}
              data-testid="execute-step-btn"
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)] flex items-center gap-1"
            >
              <SkipForward size={10} weight="bold" /> Step by Step
            </button>
          )}
          {actions.filter(a => a.type !== 'command' || !a.label.includes('Build Workflow')).map((a, i) => (
            <button
              key={i}
              onClick={() => onAction(a)}
              data-testid={`plan-action-${i}`}
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)]"
            >{a.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// PLAN EXECUTION CARD — Live execution tracking
// ═══════════════════════════════════════════════════════════════

// EXEC_STATUS_STYLES imported from ./constants

const PlanExecStepIcon = ({ status }) => {
  if (status === 'in_progress') return <CircleNotch size={13} className="animate-spin text-[#3B82F6]" />;
  if (status === 'complete') return <CheckCircle size={13} weight="fill" className="text-[#10B981]" />;
  if (status === 'failed') return <WarningCircle size={13} weight="fill" className="text-[#EF4444]" />;
  if (status === 'skipped') return <Minus size={13} weight="bold" className="text-[#6B7280]" />;
  if (status === 'approval_needed') return <Shield size={13} weight="fill" className="text-[#F59E0B]" />;
  return <div className="w-3.5 h-3.5 rounded-full border-2 border-[var(--border-default)]" />;
};

const PlanExecutionCard = ({ message, onAction }) => {
  const { planExecution = {} } = message;
  const { plan_id, goal, steps = [], status: planStatus, completed = 0, skipped = 0, failed = 0, total = 0 } = planExecution;
  const [expandedStep, setExpandedStep] = useState(null);

  const isPreview = planStatus === 'preview';
  const isComplete = planStatus === 'completed';
  const isPaused = planStatus === 'paused';
  const isFailed = planStatus === 'failed';
  const isExecuting = planStatus === 'executing';

  // Find next pending step
  const nextPending = steps.find(s => s.exec_status === 'pending');
  const progressPct = total > 0 ? Math.round(((completed + skipped) / total) * 100) : 0;

  const headerLabel = isPreview ? 'Plan Preview' : isComplete ? 'Plan Complete' : isFailed ? 'Plan Failed' : isPaused ? 'Plan Paused' : 'Executing Plan';
  const headerColor = isPreview ? '#8B5CF6' : isComplete ? '#10B981' : isFailed ? '#EF4444' : '#3B82F6';

  return (
    <div className="flex gap-3" data-testid="msg-plan-execution">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: `color-mix(in srgb, ${headerColor} 12%, transparent)` }}>
        {isPreview ? <Eye size={14} weight="bold" style={{ color: headerColor }} /> :
         isComplete ? <CheckCircle size={14} weight="fill" style={{ color: headerColor }} /> :
         isFailed ? <WarningCircle size={14} weight="fill" style={{ color: headerColor }} /> :
         isExecuting ? <CircleNotch size={14} className="animate-spin" style={{ color: headerColor }} /> :
         <Play size={14} weight="fill" style={{ color: headerColor }} />}
      </div>
      <div className="flex-1 space-y-2.5 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">{headerLabel}</span>
          {total > 0 && !isPreview && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
              style={{ background: `color-mix(in srgb, ${headerColor} 10%, transparent)`, color: headerColor }}>
              {completed}/{total} steps
            </span>
          )}
          {isPreview && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-[rgba(139,92,246,0.1)] text-[#8B5CF6]">
              {total} steps
            </span>
          )}
        </div>

        {/* Progress bar (not in preview) */}
        {total > 0 && !isPreview && (
          <div className="space-y-1">
            <div className="h-1.5 rounded-full bg-[var(--surface-3)] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${progressPct}%`, background: headerColor }}
                data-testid="plan-exec-progress"
              />
            </div>
            <div className="flex justify-between text-[10px] text-[var(--text-muted)]">
              <span>{progressPct}% complete</span>
              {failed > 0 && <span className="text-[#EF4444]">{failed} failed</span>}
              {skipped > 0 && <span>{skipped} skipped</span>}
            </div>
          </div>
        )}

        {/* Steps with rich detail */}
        {steps.length > 0 && (
          <div className="space-y-1" data-testid="plan-exec-steps">
            {steps.map((step, i) => {
              const sty = isPreview
                ? { color: 'var(--text-secondary)', bg: 'var(--surface-3)', label: step.risk === 'medium' ? 'Approval' : 'Ready' }
                : (EXEC_STATUS_STYLES[step.exec_status] || EXEC_STATUS_STYLES.pending);
              const catColor = CATEGORY_COLORS[step.category] || CATEGORY_COLORS.unknown;
              const isExpanded = expandedStep === i;
              return (
                <div key={i} className="rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] overflow-hidden"
                  data-testid={`exec-step-${i}`}>
                  {/* Step row */}
                  <div className="flex items-center gap-2 text-xs p-2 cursor-pointer hover:bg-[var(--surface-3)] transition-colors"
                    onClick={() => setExpandedStep(isExpanded ? null : i)}>
                    {isPreview
                      ? <div className="w-3.5 h-3.5 rounded-full border-2 shrink-0" style={{ borderColor: catColor }} />
                      : <PlanExecStepIcon status={step.exec_status} />
                    }
                    <span className="w-4 h-4 rounded flex items-center justify-center shrink-0"
                      style={{ background: `color-mix(in srgb, ${catColor} 12%, transparent)` }}>
                      <span className="text-[8px] font-bold" style={{ color: catColor }}>{(step.index ?? i) + 1}</span>
                    </span>
                    <span className="font-medium text-[var(--text-primary)] flex-1 truncate">{step.capability}</span>
                    {step.system && (
                      <span className="text-[9px] text-[var(--text-muted)] shrink-0 hidden sm:inline">{step.system}</span>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 rounded shrink-0" style={{ background: sty.bg, color: sty.color }}>
                      {sty.label}
                    </span>
                    {/* Step actions (non-preview) */}
                    {!isPreview && step.exec_status === 'pending' && isPaused && (
                      <div className="flex gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => onAction({ type: 'execute_plan', payload: { plan_id, mode: 'step', start_from_step: step.index } })}
                          className="text-[9px] px-1.5 py-0.5 rounded border border-[#3B82F6]/30 text-[#3B82F6] hover:bg-[#3B82F6]/10"
                          data-testid={`exec-run-${i}`}
                        >Run</button>
                        <button
                          onClick={() => onAction({ type: 'skip_step', payload: { plan_id, step_index: step.index } })}
                          className="text-[9px] px-1.5 py-0.5 rounded border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-3)]"
                          data-testid={`exec-skip-${i}`}
                        >Skip</button>
                      </div>
                    )}
                    {!isPreview && step.exec_status === 'approval_needed' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onAction({ type: 'approve_step', payload: { plan_id, step_index: step.index } }); }}
                        className="text-[9px] px-1.5 py-0.5 rounded border border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/10"
                        data-testid={`exec-approve-${i}`}
                      >Approve</button>
                    )}
                    {!isPreview && step.exec_status === 'failed' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onAction({ type: 'execute_plan', payload: { plan_id, mode: 'step', start_from_step: step.index } }); }}
                        className="text-[9px] px-1.5 py-0.5 rounded border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10"
                        data-testid={`exec-retry-${i}`}
                      >Retry</button>
                    )}
                    <CaretRight size={10} className={`shrink-0 text-[var(--text-muted)] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                  {/* Expanded detail panel */}
                  {isExpanded && (
                    <div className="px-3 pb-2.5 pt-0.5 space-y-1.5 border-t border-[var(--border-subtle)] bg-[var(--surface-1)]">
                      {/* What will happen */}
                      <div className="text-[10px]">
                        <span className="text-[var(--text-muted)] font-medium uppercase tracking-wider">Action: </span>
                        <span className="text-[var(--text-secondary)]">{step.description || step.action_type}</span>
                      </div>
                      {/* System */}
                      {step.system && (
                        <div className="text-[10px]">
                          <span className="text-[var(--text-muted)] font-medium uppercase tracking-wider">System: </span>
                          <span className="text-[var(--text-secondary)]">{step.system}</span>
                        </div>
                      )}
                      {/* Risk */}
                      <div className="text-[10px] flex items-center gap-1.5">
                        <span className="text-[var(--text-muted)] font-medium uppercase tracking-wider">Risk: </span>
                        <span className={`px-1 py-0.5 rounded ${
                          step.risk === 'high' ? 'bg-[rgba(239,68,68,0.08)] text-[#EF4444]' :
                          step.risk === 'medium' ? 'bg-[rgba(245,158,11,0.08)] text-[#F59E0B]' :
                          'bg-[rgba(16,185,129,0.08)] text-[#10B981]'
                        }`}>{step.risk || 'low'}</span>
                        {step.risk_reason && <span className="text-[var(--text-muted)]"> {step.risk_reason}</span>}
                      </div>
                      {/* Expected outcome */}
                      {step.expected_outcome && (
                        <div className="text-[10px]">
                          <span className="text-[var(--text-muted)] font-medium uppercase tracking-wider">Outcome: </span>
                          <span className="text-[var(--text-secondary)]">{step.expected_outcome.split(/(\*\*.*?\*\*)/).map((part, j) =>
                            part.startsWith('**') && part.endsWith('**')
                              ? <strong key={j} className="font-semibold text-[var(--text-primary)]">{part.slice(2, -2)}</strong>
                              : part
                          )}</span>
                        </div>
                      )}
                      {/* Result message (after execution) */}
                      {step.message && (
                        <div className="text-[10px] mt-1 p-1.5 rounded bg-[var(--surface-2)]">
                          <span className="text-[var(--text-muted)] font-medium uppercase tracking-wider">Result: </span>
                          <span className="text-[var(--text-secondary)]">{step.message.split?.(/(\*\*.*?\*\*)/).map((part, j) =>
                            part.startsWith('**') && part.endsWith('**')
                              ? <strong key={j} className="font-semibold text-[var(--text-primary)]">{part.slice(2, -2)}</strong>
                              : part
                          )}</span>
                        </div>
                      )}
                      {/* Suggested actions on failure */}
                      {step.suggested_actions?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {step.suggested_actions.map((sa, j) => (
                            <button key={j}
                              onClick={() => onAction(sa)}
                              className="text-[9px] px-1.5 py-0.5 rounded border border-[var(--border-default)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)]"
                            >{sa.label}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Preview mode actions */}
        {isPreview && (
          <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid="plan-preview-actions">
            <button
              onClick={() => onAction({
                type: 'execute_plan',
                payload: { plan_id, mode: 'staged' },
              })}
              data-testid="confirm-execute-btn"
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white flex items-center gap-1"
            >
              <Play size={10} weight="fill" /> Execute All
            </button>
            <button
              onClick={() => onAction({
                type: 'execute_plan',
                payload: { plan_id, mode: 'step' },
              })}
              data-testid="confirm-step-btn"
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)] flex items-center gap-1"
            >
              <SkipForward size={10} weight="bold" /> Step by Step
            </button>
          </div>
        )}

        {/* Resume / Continue actions */}
        {!isPreview && (isPaused || isFailed) && nextPending && (
          <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid="plan-exec-actions">
            <button
              onClick={() => onAction({
                type: 'execute_plan',
                payload: { plan_id, mode: 'staged', start_from_step: nextPending.index },
              })}
              data-testid="resume-plan-btn"
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6] hover:text-white flex items-center gap-1"
            >
              <Play size={10} weight="fill" /> Resume All
            </button>
            <button
              onClick={() => onAction({
                type: 'execute_plan',
                payload: { plan_id, mode: 'step', start_from_step: nextPending.index },
              })}
              data-testid="next-step-btn"
              className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)] flex items-center gap-1"
            >
              <SkipForward size={10} weight="bold" /> Next Step
            </button>
          </div>
        )}

        {isComplete && (
          <div className="p-2 rounded-lg bg-[rgba(16,185,129,0.06)] border border-[rgba(16,185,129,0.15)] text-xs text-[#10B981] flex items-center gap-2">
            <CheckCircle size={13} weight="fill" />
            <span>Plan executed successfully. {completed} steps completed{skipped > 0 ? `, ${skipped} skipped` : ''}.</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COVERAGE ANALYSIS CARD — Workspace capability heatmap
// ═══════════════════════════════════════════════════════════════

const CoverageAnalysisCard = ({ message, onAction }) => {
  const data = message.platformData || {};
  const { coverage_pct = 0, categories = [], workspace_stats = {} } = data;
  const actions = message.actions || [];

  return (
    <div className="flex gap-3" data-testid="msg-coverage-analysis">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 bg-[color-mix(in_srgb,#06B6D4_12%,transparent)]">
        <Stack size={14} weight="fill" style={{ color: '#06B6D4' }} />
      </div>
      <div className="flex-1 space-y-3 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Capability Coverage</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
            style={{
              background: coverage_pct >= 80 ? 'rgba(16,185,129,0.1)' : coverage_pct >= 40 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
              color: coverage_pct >= 80 ? '#10B981' : coverage_pct >= 40 ? '#F59E0B' : '#EF4444',
            }}>
            {coverage_pct}%
          </span>
        </div>

        {/* Summary */}
        <p className="text-sm text-[var(--text-primary)] leading-relaxed" data-testid="coverage-summary">
          {message.content?.split(/(\*\*.*?\*\*)/).map((part, i) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
              : part
          )}
        </p>

        {/* Category Bars */}
        {categories.length > 0 && (
          <div className="space-y-1.5" data-testid="coverage-categories">
            {categories.map((cat, i) => {
              const catColor = CATEGORY_COLORS[cat.category] || CATEGORY_COLORS.unknown;
              const CatIcon = CATEGORY_ICONS[cat.category] || Cube;
              return (
                <div key={i} className="space-y-0.5" data-testid={`coverage-cat-${cat.category}`}>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <CatIcon size={11} weight="bold" style={{ color: catColor }} />
                      <span className="text-[var(--text-primary)] capitalize">{cat.category}</span>
                    </div>
                    <span className="text-[var(--text-muted)]">{cat.covered}/{cat.total}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[var(--surface-3)] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${cat.pct}%`, background: catColor }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Workspace Stats */}
        <div className="flex gap-3 text-xs text-[var(--text-muted)]">
          <span>{workspace_stats.agents || 0} agents</span>
          <span>{workspace_stats.apps || 0} apps</span>
          <span>{workspace_stats.external || 0} external</span>
        </div>

        {/* Action chips */}
        {actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid="coverage-actions">
            {actions.map((a, i) => (
              <button
                key={i}
                onClick={() => onAction(a)}
                data-testid={`coverage-action-${i}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  a.style === 'primary'
                    ? 'border-[#06B6D4] text-[#06B6D4] hover:bg-[#06B6D4] hover:text-white'
                    : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)]'
                }`}
              >{a.label}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// NOTIFICATION CHANNEL MESSAGE — Rich card for channel management
// ═══════════════════════════════════════════════════════════════

// CHANNEL_ICONS imported from ./constants

const NotificationChannelMessage = ({ message, onAction }) => {
  const { content, platformData = {}, actions = [] } = message;
  const pdType = platformData.type;
  const API = BACKEND_URL;

  const [testing, setTesting] = React.useState(null);
  const [testResult, setTestResult] = React.useState(null);

  // Channel form state
  const [formType, setFormType] = React.useState(platformData.channel_type || 'slack');
  const [formName, setFormName] = React.useState('');
  const [formConfig, setFormConfig] = React.useState('');
  const [formSev, setFormSev] = React.useState([]);
  const [creating, setCreating] = React.useState(false);
  const [formError, setFormError] = React.useState('');
  const [formSuccess, setFormSuccess] = React.useState('');

  const handleTest = async (channelId) => {
    setTesting(channelId);
    setTestResult(null);
    try {
      const resp = await fetch(`${API}/api/alerts/channels/test/${channelId}`, {
        method: 'POST', credentials: 'include',
      });
      const data = await resp.json();
      setTestResult(data);
    } catch {
      setTestResult({ success: false, error: 'Request failed' });
    }
    setTesting(null);
  };

  const handleCreate = async () => {
    setFormError('');
    setFormSuccess('');
    if (!formName.trim()) { setFormError('Name is required'); return; }
    let parsedConfig = {};
    if (formConfig.trim()) {
      try { parsedConfig = JSON.parse(formConfig); } catch { setFormError('Invalid JSON config'); return; }
    }
    setCreating(true);
    try {
      const resp = await fetch(`${API}/api/alerts/channels/create`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formName, channel_type: formType, config: parsedConfig, severity_filter: formSev }),
      });
      if (resp.ok) {
        const ch = await resp.json();
        setFormSuccess(`Channel "${ch.name}" created (${ch.channel_id})`);
        setFormName(''); setFormConfig(''); setFormSev([]);
      } else {
        const err = await resp.json();
        setFormError(err.detail || 'Creation failed');
      }
    } catch {
      setFormError('Request failed');
    }
    setCreating(false);
  };

  const handleDelete = async (channelId) => {
    try {
      await fetch(`${API}/api/alerts/channels/delete/${channelId}`, {
        method: 'DELETE', credentials: 'include',
      });
      onAction({ type: 'command', label: 'Refresh', payload: { value: 'show notification channels' } });
    } catch { /* ignore */ }
  };

  const toggleSev = (sev) => setFormSev(prev => prev.includes(sev) ? prev.filter(s => s !== sev) : [...prev, sev]);

  return (
    <div className="flex gap-3" data-testid="msg-notification-channels">
      <div className="w-7 h-7 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0 mt-0.5">
        <Bell size={14} weight="fill" className="text-[var(--brand-primary)]" />
      </div>
      <div className="flex-1 space-y-3 min-w-0">

        {/* Channel list */}
        {pdType === 'notification_channels' && platformData.channels?.length > 0 && (
          <div className="space-y-2" data-testid="channel-list">
            <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Notification Channels</span>
            <div className="space-y-1.5">
              {platformData.channels.map((ch) => {
                const meta = CHANNEL_ICONS[ch.channel_type] || CHANNEL_ICONS.webhook;
                const ChIcon = meta.icon;
                return (
                  <div key={ch.channel_id} className="p-2.5 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] flex items-center gap-3" data-testid={`channel-${ch.channel_id}`}>
                    <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ background: `color-mix(in srgb, ${meta.color} 14%, transparent)` }}>
                      <ChIcon size={13} weight="fill" style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--text-primary)] truncate">{ch.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ch.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-500/10 text-gray-400'}`}>{ch.status}</span>
                      </div>
                      <div className="text-xs text-[var(--text-muted)]">
                        {ch.channel_type} | Sent: {ch.send_count || 0} | Errors: {ch.error_count || 0}
                        {ch.severity_filter?.length > 0 && ` | Filter: ${ch.severity_filter.join(', ')}`}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => handleTest(ch.channel_id)} disabled={testing === ch.channel_id}
                        className="px-2 py-1 rounded text-[10px] font-medium border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)] transition-all disabled:opacity-50"
                        data-testid={`test-channel-${ch.channel_id}`}
                      >{testing === ch.channel_id ? 'Testing...' : 'Test'}</button>
                      <button onClick={() => handleDelete(ch.channel_id)}
                        className="px-1.5 py-1 rounded text-[10px] border border-[rgba(239,68,68,0.2)] text-[#F87171] hover:bg-[rgba(239,68,68,0.1)] transition-all"
                        data-testid={`delete-channel-${ch.channel_id}`}
                      ><Trash size={11} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
            {testResult && (
              <div className={`p-2 rounded text-xs ${testResult.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`} data-testid="test-result">
                {testResult.success ? 'Test notification sent successfully' : `Test failed: ${testResult.error}`}
              </div>
            )}
          </div>
        )}

        {/* Channel form */}
        {pdType === 'channel_form' && (
          <div className="space-y-2.5" data-testid="channel-form">
            <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Add Channel</span>
            <div className="p-3 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] space-y-2.5">
              <div className="flex gap-1.5">
                {['slack', 'webhook', 'email'].map(t => (
                  <button key={t} onClick={() => setFormType(t)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${formType === t ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] bg-[var(--brand-primary)]/10' : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
                    data-testid={`form-type-${t}`}
                  >{t.charAt(0).toUpperCase() + t.slice(1)}</button>
                ))}
              </div>
              <input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Channel name" data-testid="channel-name-input"
                className="w-full px-3 py-1.5 rounded-md text-sm bg-[var(--surface-1)] border border-[var(--border-default)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)]" />
              <textarea value={formConfig} onChange={e => setFormConfig(e.target.value)} rows={3}
                placeholder={formType === 'slack' ? '{"webhook_url": "https://hooks.slack.com/..."}' : formType === 'email' ? '{"recipients": ["ops@company.com"]}' : '{"webhook_url": "https://..."}'}
                data-testid="channel-config-input"
                className="w-full px-3 py-1.5 rounded-md text-xs font-mono bg-[var(--surface-1)] border border-[var(--border-default)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)] resize-none" />
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--text-muted)]">Severity:</span>
                {['critical', 'warning', 'info'].map(sev => (
                  <button key={sev} onClick={() => toggleSev(sev)}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-all ${formSev.includes(sev) ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]' : 'border-[var(--border-default)] text-[var(--text-muted)]'}`}
                    data-testid={`sev-filter-${sev}`}
                  >{sev}</button>
                ))}
                <span className="text-[10px] text-[var(--text-muted)]">{formSev.length === 0 ? '(all)' : ''}</span>
              </div>
              {formError && <p className="text-xs text-red-400" data-testid="form-error">{formError}</p>}
              {formSuccess && <p className="text-xs text-emerald-400" data-testid="form-success">{formSuccess}</p>}
              <button onClick={handleCreate} disabled={creating} data-testid="create-channel-btn"
                className="px-4 py-1.5 rounded-full text-xs font-medium border border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white transition-all disabled:opacity-50"
              >{creating ? 'Creating...' : 'Create Channel'}</button>
            </div>
          </div>
        )}

        {/* Channel test picker */}
        {pdType === 'channel_test' && platformData.channels?.length > 0 && (
          <div className="space-y-2" data-testid="channel-test-picker">
            <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Test Notification</span>
            <div className="space-y-1.5">
              {platformData.channels.map((ch) => {
                const meta = CHANNEL_ICONS[ch.channel_type] || CHANNEL_ICONS.webhook;
                const ChIcon = meta.icon;
                return (
                  <div key={ch.channel_id} className="p-2.5 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] flex items-center gap-3">
                    <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: `color-mix(in srgb, ${meta.color} 14%, transparent)` }}>
                      <ChIcon size={11} weight="fill" style={{ color: meta.color }} />
                    </div>
                    <span className="text-sm text-[var(--text-primary)] flex-1">{ch.name}</span>
                    <button onClick={() => handleTest(ch.channel_id)} disabled={testing === ch.channel_id}
                      className="px-3 py-1 rounded-full text-xs font-medium border border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white transition-all disabled:opacity-50"
                      data-testid={`test-pick-${ch.channel_id}`}
                    >{testing === ch.channel_id ? 'Sending...' : 'Send Test'}</button>
                  </div>
                );
              })}
            </div>
            {testResult && (
              <div className={`p-2 rounded text-xs ${testResult.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`} data-testid="test-result">
                {testResult.success ? 'Test notification sent' : `Failed: ${testResult.error}`}
              </div>
            )}
          </div>
        )}

        {/* Rendered markdown message (always shown) */}
        {content && (
          <div className="text-sm text-[var(--text-secondary)] leading-relaxed space-y-1" data-testid="channel-message-content">
            {content.split('\n').map((line, i) => {
              const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[var(--text-primary)]">$1</strong>').replace(/`(.*?)`/g, '<code class="px-1 py-0.5 rounded bg-[var(--surface-3)] text-[var(--text-primary)] text-xs">$1</code>');
              return <p key={i} dangerouslySetInnerHTML={{ __html: formatted || '&nbsp;' }} />;
            })}
          </div>
        )}

        {/* Action chips */}
        {actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid="channel-actions">
            {actions.map((a, i) => (
              <button key={i} onClick={() => onAction(a)} data-testid={`channel-action-${i}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${a.style === 'primary' ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white' : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)]'}`}
              >{a.label}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ── ProfileNudgeMessage: Inline, dismissible nudge when Command needs missing profile context ──
const ProfileNudgeMessage = ({ message }) => {
  const [dismissed, setDismissed] = useState(false);
  const nudge = message.nudgeData;

  const handleDismiss = () => {
    setDismissed(true);
    if (nudge?.field_label) {
      try { sessionStorage.setItem(`profile_nudge_${nudge.field_label}`, '1'); } catch {}
    }
  };

  if (dismissed || !nudge) return null;

  return (
    <div className="flex gap-3" data-testid="profile-nudge-message">
      <div className="w-7 h-7 rounded-lg bg-[rgba(245,158,11,0.1)] flex items-center justify-center shrink-0 mt-0.5">
        <Info size={14} weight="fill" className="text-amber-400" />
      </div>
      <div className="flex-1 bg-[rgba(245,158,11,0.04)] border border-[rgba(245,158,11,0.12)] rounded-xl px-3.5 py-2.5">
        <p className="text-xs text-[#F0F4F8] leading-relaxed">{message.content}</p>
        <div className="flex items-center gap-3 mt-2">
          <a
            href={nudge.cta_route}
            onClick={(e) => {
              e.preventDefault();
              handleDismiss();
              window.location.href = nudge.cta_route;
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-[rgba(245,158,11,0.1)] text-amber-400 border border-[rgba(245,158,11,0.2)] hover:bg-[rgba(245,158,11,0.18)] transition-all"
            data-testid="profile-nudge-cta"
          >
            <ArrowRight size={11} />
            {nudge.cta_label}
          </a>
          <button
            onClick={handleDismiss}
            className="text-[10px] text-[#7D8896] hover:text-[#B0B8C4] transition-colors"
            data-testid="profile-nudge-dismiss"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   CONNECT APP INTENT DETECTION + COINS
   ═══════════════════════════════════════════════════════════════ */

const CONNECT_PATTERNS = [
  /\b(?:connect|add|link|integrate|set\s*up|enable)\s+(\w[\w\s]*)/i,
  /\b(\w[\w\s]*)\s+(?:integration|connection)\b/i,
];

/**
 * Detect connect_app intent from actions, resolved intent, or preceding user text.
 * Returns array of { appName, slug } for recognized connectable providers.
 */
function detectConnectApps(message, messages) {
  const appNames = Object.keys(APP_SLUG_MAP);
  const found = new Set();

  // 1. Check actions from backend (primary signal)
  if (message.actions?.length > 0) {
    for (const action of message.actions) {
      // Actions that navigate to apps/integrations page, or have connect-related labels
      const label = (action.label || '').toLowerCase();
      const route = action.payload?.route || action.target || '';
      if (label.includes('connect') || route.includes('/apps') || route.includes('connect')) {
        // Try to extract app name from the label or route
        for (const name of appNames) {
          if (label.includes(name.toLowerCase()) || route.includes(getSlug(name))) {
            found.add(name);
          }
        }
      }
    }
  }

  // 2. Check resolved intent from backend
  if (message.resolvedIntent) {
    const intent = (typeof message.resolvedIntent === 'string' ? message.resolvedIntent : message.resolvedIntent?.type || '').toLowerCase();
    if (intent.includes('connect') || intent.includes('integration') || intent.includes('app_install')) {
      // Scan message content for app names
      const text = (message.content || '').toLowerCase();
      for (const name of appNames) {
        if (text.includes(name.toLowerCase())) {
          found.add(name);
        }
      }
    }
  }

  // 3. Fallback: check the preceding user message for connect patterns
  if (found.size === 0 && messages?.length > 0) {
    // Find the user message that preceded this assistant message
    const msgIndex = messages.findIndex(m => m.id === message.id);
    if (msgIndex > 0) {
      for (let i = msgIndex - 1; i >= Math.max(0, msgIndex - 2); i--) {
        if (messages[i].type === 'user') {
          const userText = (messages[i].content || '').toLowerCase();
          // Check if user text matches connect patterns
          for (const pattern of CONNECT_PATTERNS) {
            const match = userText.match(pattern);
            if (match) {
              const extracted = (match[1] || '').trim().toLowerCase();
              for (const name of appNames) {
                if (name.toLowerCase().includes(extracted) || extracted.includes(name.toLowerCase())) {
                  found.add(name);
                }
              }
            }
          }
          // Also do direct name matching
          for (const name of appNames) {
            if (userText.includes(name.toLowerCase()) && (userText.includes('connect') || userText.includes('add') || userText.includes('link') || userText.includes('integrate'))) {
              found.add(name);
            }
          }
          break;
        }
      }
    }
  }

  return [...found].map(name => ({ appName: name, slug: getSlug(name) }));
}

/**
 * ConnectAppCoins — renders clickable app pills for connect intents.
 */
const ConnectAppCoins = ({ apps, connectedApps, connectingApp, onConnectApp }) => {
  if (!apps?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 pt-1.5" data-testid="connect-app-coins">
      {apps.map(({ appName, slug }) => {
        const isConnected = connectedApps?.has(slug);
        const isConnecting = connectingApp === slug;
        return (
          <button
            key={slug}
            onClick={() => !isConnected && !isConnecting && onConnectApp?.(appName)}
            disabled={isConnecting}
            data-testid={`connect-coin-${slug}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
              isConnected
                ? 'bg-[rgba(0,229,192,0.06)] border-[rgba(0,229,192,0.15)] text-[#7D8896] cursor-default'
                : 'bg-[#243041] border-[rgba(255,255,255,0.08)] text-[#F0F4F8] hover:bg-[#2A3648] hover:border-[rgba(255,255,255,0.15)] cursor-pointer active:scale-[0.97]'
            }`}
          >
            <img
              src={getAppLogo(appName)}
              alt={appName}
              className="w-4 h-4 rounded-sm object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {isConnecting ? (
              <><CircleNotch size={10} className="animate-spin" /> Connecting...</>
            ) : isConnected ? (
              <><CheckCircle size={10} weight="fill" className="text-[#00E5C0]" /> {appName}</>
            ) : (
              appName
            )}
          </button>
        );
      })}
    </div>
  );
};


/**
 * ICON_MAP — maps icon name strings from the backend to Phosphor icon components.
 */
const NAV_ICON_MAP = {
  Exam,
  ChartBar,
  Robot,
  Plus,
  Phone,
  TreeStructure,
  Plugs,
  CreditCard,
  House,
  Bell,
  UsersThree,
  UserCircle,
  Question,
  Brain,
  ArrowsLeftRight,
  Gear,
  Storefront,
  NavigationArrow,
};

/**
 * NavigationCoins — renders clickable coin-style buttons for navigate actions.
 * Similar pattern to ConnectAppCoins but for platform page navigation.
 */
const NavigationCoins = ({ actions, onAction }) => {
  const navActions = (actions || []).filter(a => a.type === 'navigate' && a.payload?.route);
  if (!navActions.length) return null;

  return (
    <div className="flex flex-wrap gap-2 pt-1.5" data-testid="navigation-coins">
      {navActions.map((a, i) => {
        const IconComponent = NAV_ICON_MAP[a.icon] || NavigationArrow;
        return (
          <button
            key={i}
            onClick={() => onAction(a)}
            data-testid={`nav-coin-${i}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border bg-[#243041] border-[rgba(0,229,192,0.12)] text-xs font-medium text-[#F0F4F8] hover:bg-[#2A3648] hover:border-[rgba(0,229,192,0.3)] cursor-pointer active:scale-[0.97] transition-all"
          >
            <span className="w-4 h-4 rounded-sm flex items-center justify-center bg-[rgba(0,229,192,0.1)]">
              <IconComponent size={11} weight="duotone" className="text-[#00E5C0]" />
            </span>
            {a.label}
          </button>
        );
      })}
    </div>
  );
};


const MessageBubble = ({ message, onAction, onSuggestion, onRun, onViewDetails, isBasicTier, connectedApps, connectingApp, onConnectApp, messages }) => {
  const { type, content } = message;

  // --- EXECUTION ---
  if (type === 'execution') {
    return <ExecutionMessage message={message} onAction={onAction} />;
  }

  // --- ORCHESTRATION ---
  if (type === MSG.ORCHESTRATION) {
    return <OrchestrationMessage message={message} onAction={onAction} />;
  }

  // --- CREATION ---
  if (type === MSG.CREATION) {
    return <CreationMessage message={message} onAction={onAction} />;
  }

  // --- MODIFICATION ---
  if (type === MSG.MODIFICATION) {
    return <WorkflowChangeCard message={message} onAction={onAction} />;
  }

  // --- BUILDER STEP (Command ↔ Builder binding) ---
  if (type === MSG.BUILDER_STEP) {
    return <BuilderStepMessage message={message} />;
  }

  // --- BUILDER SKILL (Phase B: Feedback & Self-Learning) ---
  if (type === MSG.BUILDER_SKILL) {
    return <BuilderSkillMessage message={message} />;
  }

  // --- RUN EXPLANATION ---
  if (type === MSG.RUN_EXPLANATION) {
    return <RunExplanationCard message={message} onAction={onAction} />;
  }

  // --- CLONE TEMPLATE ---
  if (type === MSG.CLONE_TEMPLATE) {
    return <CloneTemplateMessage message={message} onAction={onAction} />;
  }

  // --- NOTIFICATION CHANNELS ---
  if (type === MSG.NOTIFICATION_CHANNELS) {
    return <NotificationChannelMessage message={message} onAction={onAction} />;
  }

  // --- CAPABILITY PLAN ---
  if (type === MSG.CAPABILITY_PLAN) {
    return <CapabilityPlanCard message={message} onAction={onAction} />;
  }

  // --- PLAN EXECUTION ---
  if (type === MSG.PLAN_EXECUTION) {
    return <PlanExecutionCard message={message} onAction={onAction} />;
  }

  // --- PROFILE NUDGE (inline, dismissible) ---
  if (type === 'profile_nudge') {
    return <ProfileNudgeMessage message={message} />;
  }

  // --- USER ---
  if (type === MSG.USER) {
    return (
      <div className="flex justify-end" data-testid="msg-user">
        <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-br-md bg-[var(--brand-primary)] text-[#0A0E14] text-sm leading-relaxed font-medium">
          {content}
        </div>
      </div>
    );
  }

  // --- WELCOME ---
  if (type === MSG.WELCOME) {
    const voiceEnabled = isSpeechRecognitionSupported();
    return (
      <div className="space-y-3" data-testid="msg-welcome">
        <div className="flex gap-3">
          <div className="w-7 h-7 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0 mt-0.5">
            <Lightning size={14} weight="fill" className="text-[var(--brand-primary)]" />
          </div>
          <div className="space-y-2 flex-1">
            <p className="text-[13px] font-semibold text-[var(--text-primary)]">
              Build and manage your workers
            </p>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              Create workers, connect tools, and update workflows from one place.
            </p>
            <div className="flex flex-col gap-2 pt-1 md:flex-row md:flex-wrap md:gap-1.5">
              {message.suggestions?.map((s, i) => (
                <button
                  key={i}
                  onClick={() => onSuggestion(s.value)}
                  className="w-full md:w-auto px-4 py-2.5 md:px-3 md:py-1.5 rounded-xl md:rounded-full text-sm md:text-xs font-medium border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)] transition-all active:scale-[0.98] text-left"
                  data-testid={`suggestion-${i}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {voiceEnabled && (
              <p className="text-xs text-[var(--text-muted)] flex items-center gap-1.5 pt-1">
                <Microphone size={11} />
                Hold the mic to speak — Command only listens while you hold.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- ASSISTANT (supports streaming + structured actions) ---
  if (type === MSG.ASSISTANT) {
    return (
      <div className="flex gap-3" data-testid="msg-assistant">
        <div className="w-7 h-7 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0 mt-0.5">
          <Lightning size={14} weight="fill" className="text-[var(--brand-primary)]" />
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          {/* Thinking indicator */}
          {message.thinking && !content && (
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] animate-pulse" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] animate-pulse" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] animate-pulse" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-xs text-[var(--text-muted)]">Thinking...</span>
            </div>
          )}

          {/* Message text */}
          {content && (
            <p className="text-sm text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap">
              {content}
              {message.streaming && <span className="inline-block w-0.5 h-4 bg-[var(--brand-primary)] ml-0.5 animate-pulse align-text-bottom" />}
            </p>
          )}

          {/* Orchestration plan */}
          {message.orchestration?.steps?.length > 0 && (
            <div className="p-3 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)] space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider flex items-center gap-1.5">
                  <FlowArrow size={12} /> Execution Plan
                </p>
                {message.orchestration.all_resolved && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgba(16,185,129,0.1)] text-[#10B981]">Ready</span>
                )}
              </div>
              {message.orchestration.steps.map((step, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-[var(--text-muted)] w-5 shrink-0">{i + 1}.</span>
                  <span className={`text-xs ${step.resolved !== false ? 'text-[var(--text-primary)]' : 'text-[#F87171]'}`}>
                    {step.agent_name}
                  </span>
                  {step.resolved === false && (
                    <span className="text-[10px] text-[#F87171]">(not found)</span>
                  )}
                  {i < message.orchestration.steps.length - 1 && (
                    <ArrowRight size={10} className="text-[var(--text-muted)] opacity-40" />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Inline results */}
          {message.results?.length > 0 && (
            <div className="space-y-1.5">
              {message.results.map((r) => (
                <CompactResultCard
                  key={r.id}
                  agent={r}
                  onRun={onRun}
                  onViewDetails={onViewDetails}
                  isBasicTier={isBasicTier}
                />
              ))}
            </div>
          )}

          {/* Structured action chips (non-navigate) */}
          {message.actions?.length > 0 && !message.streaming && (() => {
            const nonNavActions = message.actions.filter(a => a.type !== 'navigate');
            return nonNavActions.length > 0 ? (
              <div className="flex flex-col gap-2 pt-0.5 md:flex-row md:flex-wrap md:gap-1.5">
                {nonNavActions.map((a, i) => (
                  <button
                    key={i}
                    onClick={() => onAction(a)}
                    className={`w-full md:w-auto px-4 py-2.5 md:px-3 md:py-1.5 rounded-xl md:rounded-full text-sm md:text-xs font-medium border transition-all active:scale-[0.98] ${
                      a.style === 'primary'
                        ? 'border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-[#0B1220]'
                        : a.style === 'danger'
                        ? 'border-[rgba(239,68,68,0.3)] text-[#F87171] hover:bg-[rgba(239,68,68,0.1)]'
                        : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)]'
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            ) : null;
          })()}

          {/* Navigation Coins (coin-style buttons for navigate actions) */}
          {message.actions?.length > 0 && !message.streaming && (
            <NavigationCoins actions={message.actions} onAction={onAction} />
          )}

          {/* Connect App Coins (rendered when connect intent detected) */}
          {!message.streaming && (() => {
            const apps = detectConnectApps(message, messages);
            return apps.length > 0 ? (
              <ConnectAppCoins
                apps={apps}
                connectedApps={connectedApps}
                connectingApp={connectingApp}
                onConnectApp={onConnectApp}
              />
            ) : null;
          })()}
        </div>
      </div>
    );
  }

  // --- RESULTS (standalone, from Phase 1 fallback) ---
  if (type === MSG.RESULTS) {
    return (
      <div className="flex gap-3" data-testid="msg-results">
        <div className="w-7 h-7 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0 mt-0.5">
          <Lightning size={14} weight="fill" className="text-[var(--brand-primary)]" />
        </div>
        <div className="flex-1 space-y-2">
          {content && <p className="text-sm text-[var(--text-primary)] leading-relaxed">{content}</p>}
          <div className="space-y-1.5">
            {message.results?.map((agent) => (
              <CompactResultCard
                key={agent.agent_id || agent.id}
                agent={agent}
                onRun={onRun}
                onViewDetails={onViewDetails}
                isBasicTier={isBasicTier}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // --- CONFIRMATION ---
  if (type === MSG.CONFIRMATION) {
    return (
      <div className="flex gap-3" data-testid="msg-confirmation">
        <div className="w-7 h-7 rounded-lg bg-[rgba(16,185,129,0.15)] flex items-center justify-center shrink-0 mt-0.5">
          <CheckCircle size={14} weight="fill" className="text-[#10B981]" />
        </div>
        <p className="text-sm text-[#10B981] leading-relaxed">{content}</p>
      </div>
    );
  }

  // --- WARNING ---
  if (type === MSG.WARNING) {
    return (
      <div className="flex gap-3" data-testid="msg-warning">
        <div className="w-7 h-7 rounded-lg bg-[rgba(239,68,68,0.12)] flex items-center justify-center shrink-0 mt-0.5">
          <WarningCircle size={14} weight="fill" className="text-[#F87171]" />
        </div>
        <div className="flex-1">
          <p className="text-sm text-[#F87171] leading-relaxed">{content}</p>
          {message.actions?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {message.actions.map((a, i) => (
                <button key={i} onClick={() => onAction(a)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border border-[rgba(239,68,68,0.3)] text-[#F87171] hover:bg-[rgba(239,68,68,0.1)] transition-all"
                >{a.label}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- SYSTEM ---
  if (type === MSG.SYSTEM) {
    // Live event messages have distinct styling
    if (message.liveEvent) {
      const severityStyles = {
        high: 'border-red-500/25 bg-red-500/6 text-red-300',
        medium: 'border-amber-500/25 bg-amber-500/6 text-amber-300',
        low: 'border-blue-500/20 bg-blue-500/5 text-blue-300',
      };
      const style = severityStyles[message.severity] || severityStyles.medium;
      return (
        <div className="px-1" data-testid="msg-live-event">
          <div className={`px-3 py-2 rounded-lg border ${style} text-xs`}>
            <div className="flex items-start gap-2">
              <span className="shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
              <div className="flex-1 min-w-0">
                <p className="leading-relaxed" dangerouslySetInnerHTML={{
                  __html: content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                }} />
                {message.actions?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {message.actions.map((action, i) => (
                      <button
                        key={i}
                        onClick={() => onAction?.(action)}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${
                          action.style === 'primary'
                            ? 'border-current bg-current/10 hover:bg-current/20'
                            : 'border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                        }`}
                        data-testid={`live-action-${i}`}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-center" data-testid="msg-system">
        <p className="text-xs text-[var(--text-muted)] px-3 py-1 rounded-full bg-[var(--surface-1)]">
          {content}
        </p>
      </div>
    );
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════
// DETAIL DRAWER — Secondary panel for rich content
// ═══════════════════════════════════════════════════════════════

const DetailDrawer = ({ isOpen, agent, onClose, onRun, isBasicTier }) => {
  if (!isOpen || !agent) return null;

  const config = COMMAND_MODAL_CONFIGS[agent.run_modal_variant] || COMMAND_MODAL_CONFIGS.fixed_intent_text_only;
  const isVoice = agent.capability_profile === 'live_voice_s2s_agent';

  const inputTypes = (() => {
    const inputs = agent.accepted_inputs || ['text'];
    const types = [];
    if (inputs.includes('text')) types.push('Text');
    if (inputs.some(i => i.startsWith('file_'))) types.push('Files');
    if (inputs.includes('audio')) types.push('Audio');
    if (inputs.includes('video')) types.push('Video');
    return types;
  })();

  return (
    <div className="absolute inset-y-0 right-0 w-full max-w-sm bg-[var(--surface-1)] border-l border-[var(--border-default)] z-30 flex flex-col shadow-xl animate-slide-in-right" data-testid="detail-drawer">
      {/* Header */}
      <div className="p-4 border-b border-[var(--border-subtle)] flex items-center justify-between">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-[var(--surface-3)] flex items-center justify-center shrink-0">
            {isVoice ? <Phone size={15} className="text-[var(--brand-secondary)]" /> : <Robot size={15} className="text-[var(--brand-primary)]" />}
          </div>
          <h2 className="text-sm font-semibold text-[var(--text-primary)] truncate">{agent.name}</h2>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--surface-3)] transition-colors" data-testid="detail-drawer-close">
          <X size={16} className="text-[var(--text-muted)]" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{agent.short_description || config.description}</p>

        {/* Capabilities */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">Accepts</h3>
          <div className="flex flex-wrap gap-1.5">
            {inputTypes.map((t, i) => (
              <span key={i} className="px-2.5 py-1 rounded-md text-xs bg-[var(--surface-3)] text-[var(--text-secondary)]">{t}</span>
            ))}
          </div>
        </div>

        {isVoice && (
          <div className="p-3 rounded-lg bg-[rgba(236,72,153,0.08)] border border-[rgba(236,72,153,0.15)]">
            <p className="text-xs text-[var(--brand-secondary)] flex items-start gap-2">
              <Phone size={13} className="shrink-0 mt-0.5" />
              This is a live voice agent. It runs via telephony and cannot be started from Command.
            </p>
          </div>
        )}

        {agent.last_run_at && (
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <Clock size={12} />
            Last run: {new Date(agent.last_run_at).toLocaleDateString()}
          </div>
        )}

        {isBasicTier && (
          <div className="p-3 rounded-lg bg-[rgba(99,102,241,0.08)] border border-[rgba(99,102,241,0.15)]">
            <p className="text-xs text-[var(--text-secondary)] flex items-start gap-2">
              <Eye size={13} className="shrink-0 mt-0.5 text-[var(--brand-primary)]" />
              Preview Mode — Upgrade to Pro to execute agents.
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      {!isVoice && (
        <div className="p-4 border-t border-[var(--border-subtle)]">
          <button
            onClick={() => onRun(agent)}
            className={`w-full py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
              isBasicTier
                ? 'bg-[var(--surface-3)] text-[var(--text-muted)] cursor-not-allowed'
                : 'bg-[var(--brand-primary)] text-white hover:opacity-90'
            }`}
            disabled={isBasicTier}
            data-testid="detail-drawer-run"
          >
            {isBasicTier ? <><Eye size={15} /> Preview Only</> : <><Play size={15} weight="fill" /> Open & Run Agent</>}
          </button>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// EXECUTION LOCKED MODAL — Basic tier guard (preserved)
// ═══════════════════════════════════════════════════════════════

const ExecutionLockedModal = ({ isOpen, onClose, onUpgrade }) => {
  if (!isOpen) return null;
  return (
    <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-40 p-4" data-testid="execution-locked-modal">
      <div className="bg-[var(--surface-2)] rounded-xl max-w-sm w-full p-5 border border-[var(--border-default)]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--brand-primary)] flex items-center justify-center">
            <Shield size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[var(--text-primary)]">Execution Locked</h2>
            <p className="text-xs text-[var(--text-muted)]">Preview Mode Active</p>
          </div>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mb-4 leading-relaxed">
          Agent execution is available on Pro and Partner Pro plans. You can explore and browse agents in Preview Mode.
        </p>
        <div className="space-y-2">
          <button onClick={onUpgrade} className="w-full py-2.5 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
            <Crown size={15} /> Upgrade to Pro
          </button>
          <button onClick={onClose} className="w-full py-2 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            Continue Exploring
          </button>
        </div>
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// BUILDER STEP MESSAGE — Command ↔ Builder real-time binding card
// ═══════════════════════════════════════════════════════════════

const STEP_TYPE_STYLES = {
  trigger:   { badge: 'Trigger', color: '#00E5C0', bg: 'rgba(0,229,192,0.08)' },
  input:     { badge: 'Input',   color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
  action:    { badge: 'Action',  color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
  condition: { badge: 'Condition', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  output:    { badge: 'Output',  color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
};

const BuilderStepMessage = ({ message }) => {
  const { builderPhase, builderData, builderError } = message;

  const isAnalyzing = builderPhase === 'analyzing';
  const isParsed = builderPhase === 'parsed';
  const isApplied = builderPhase === 'applied' || builderPhase === 'done';
  const isError = builderPhase === 'error';

  return (
    <div className="flex gap-3" data-testid="msg-builder-step">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
        isError ? 'bg-[rgba(239,68,68,0.12)]' : 'bg-[rgba(0,229,192,0.12)]'
      }`}>
        {isAnalyzing ? (
          <CircleNotch size={14} className="animate-spin text-[#00E5C0]" />
        ) : isError ? (
          <WarningCircle size={14} className="text-[#F87171]" />
        ) : (
          <FlowArrow size={14} className="text-[#00E5C0]" />
        )}
      </div>

      <div className="flex-1 min-w-0 space-y-2">
        {/* Phase indicator */}
        {isAnalyzing && (
          <p className="text-sm text-[var(--text-secondary)]">Understanding your request...</p>
        )}

        {/* Error */}
        {isError && (
          <p className="text-sm text-[#F87171]">{builderError || 'Failed to modify steps.'}</p>
        )}

        {/* Plan summary */}
        {builderData?.plan_summary && !isAnalyzing && (
          <p className="text-sm text-[var(--text-primary)]">{builderData.plan_summary}</p>
        )}

        {/* Operations log — what was changed */}
        {builderData?.operations?.length > 0 && !isAnalyzing && (
          <div className="space-y-1 mt-1.5">
            {builderData.operations.map((op, i) => {
              const stepStyle = STEP_TYPE_STYLES[op.step?.type] || STEP_TYPE_STYLES.action;
              return (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)]">
                  {op.op === 'add' && <Plus size={12} className="text-[#00E5C0]" />}
                  {op.op === 'remove' && <Minus size={12} className="text-[#F87171]" />}
                  {op.op === 'update' && <Wrench size={12} className="text-[#3B82F6]" />}
                  {op.op === 'reorder' && <ArrowRight size={12} className="text-[#F59E0B]" />}
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider" style={{ background: stepStyle.bg, color: stepStyle.color }}>
                    {stepStyle.badge}
                  </span>
                  <span className="text-xs text-[var(--text-secondary)] flex-1 truncate">
                    {op.op === 'add' && op.step?.label}
                    {op.op === 'remove' && `Removed: ${op.removed_step?.label || ''}`}
                    {op.op === 'update' && `${op.old_label} → ${op.new_label}`}
                    {op.op === 'reorder' && `Moved step from position ${op.from} to ${op.to}`}
                  </span>
                  {(isApplied) && <CheckCircle size={12} weight="fill" className="text-[#00E5C0] shrink-0" />}
                </div>
              );
            })}
          </div>
        )}

        {/* Conversational response */}
        {isApplied && builderData?.message && (
          <p className="text-sm text-[var(--text-secondary)] mt-1">{builderData.message}</p>
        )}

        {/* Pipeline status */}
        {isApplied && builderData?.step_count != null && (
          <p className="text-[10px] text-[var(--text-muted)] mt-1">
            Pipeline: {builderData.step_count} step{builderData.step_count !== 1 ? 's' : ''}
            {builderData.previous_count != null && builderData.step_count !== builderData.previous_count && (
              <span className="ml-1 text-[#00E5C0]">
                ({builderData.step_count > builderData.previous_count ? '+' : ''}{builderData.step_count - builderData.previous_count})
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// BUILDER SKILL MESSAGE — Phase B: Feedback & Self-Learning
// ═══════════════════════════════════════════════════════════════

const SKILL_CATEGORY_STYLES = {
  tone:        { label: 'Tone',        icon: ChatCircle, color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
  rules:       { label: 'Rule',        icon: ShieldCheck, color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  knowledge:   { label: 'Knowledge',   icon: Lightning,   color: '#00E5C0', bg: 'rgba(0,229,192,0.08)' },
  preferences: { label: 'Preference',  icon: Star,        color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
  constraints: { label: 'Constraint',  icon: WarningCircle, color: '#EF4444', bg: 'rgba(239,68,68,0.08)' },
};

const BuilderSkillMessage = ({ message }) => {
  const { skillPhase, skillData, skillError } = message;

  const isAnalyzing = skillPhase === 'analyzing';
  const isParsed = skillPhase === 'parsed' || skillPhase === 'saved';
  const isDone = skillPhase === 'done';
  const isError = skillPhase === 'error';
  const skill = skillData?.skill;
  const isValid = skillData?.is_valid !== false;
  const catStyle = SKILL_CATEGORY_STYLES[skill?.category] || SKILL_CATEGORY_STYLES.knowledge;
  const CatIcon = catStyle.icon;

  return (
    <div className="flex gap-3" data-testid="msg-builder-skill">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
        isError ? 'bg-[rgba(239,68,68,0.12)]' : 'bg-[rgba(139,92,246,0.12)]'
      }`}>
        {isAnalyzing ? (
          <CircleNotch size={14} className="animate-spin text-[#8B5CF6]" />
        ) : isError ? (
          <WarningCircle size={14} className="text-[#F87171]" />
        ) : (
          <Lightning size={14} className="text-[#8B5CF6]" />
        )}
      </div>

      <div className="flex-1 min-w-0 space-y-2">
        {isAnalyzing && (
          <p className="text-sm text-[var(--text-secondary)]">Learning this...</p>
        )}

        {isError && (
          <p className="text-sm text-[#F87171]">{skillError || 'Failed to learn skill.'}</p>
        )}

        {/* Skill card */}
        {skill && isValid && !isAnalyzing && (
          <div className="px-3 py-3 rounded-lg bg-[var(--surface-2)] border border-[var(--border-subtle)]">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider" style={{ background: catStyle.bg, color: catStyle.color }}>
                <CatIcon size={10} /> {catStyle.label}
              </span>
              {(isDone || isParsed) && skillData?.saved && (
                <span className="inline-flex items-center gap-1 text-[9px] text-[#00E5C0]">
                  <CheckCircle size={10} weight="fill" /> Saved
                </span>
              )}
            </div>
            <p className="text-sm text-[var(--text-primary)]">{skill.instruction}</p>
          </div>
        )}

        {/* Not a valid skill */}
        {!isValid && !isAnalyzing && !isError && skillData?.message && (
          <p className="text-sm text-[var(--text-secondary)]">{skillData.message}</p>
        )}

        {/* Conversational response */}
        {(isDone || isParsed) && isValid && skillData?.message && (
          <p className="text-sm text-[var(--text-secondary)]">{skillData.message}</p>
        )}

        {/* Total skills count */}
        {isDone && skillData?.total_skills != null && isValid && (
          <p className="text-[10px] text-[var(--text-muted)]">
            Worker now has {skillData.total_skills} skill{skillData.total_skills !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════
export {
  ActionChipRow,
  CommandBarMic,
  ExecutionStep,
  ExecutionMessage,
  OrchestrationStep,
  OrchestrationMessage,
  CreationStepIcon,
  CreationMessage,
  WorkflowChangeCard,
  CompactResultCard,
  QuickActionChips,
  CloneTemplateMessage,
  RunExplanationCard,
  CapabilityPlanCard,
  PlanExecStepIcon,
  PlanExecutionCard,
  CoverageAnalysisCard,
  NotificationChannelMessage,
  BuilderStepMessage,
  BuilderSkillMessage,
  NavigationCoins,
  MessageBubble,
  DetailDrawer,
  ExecutionLockedModal,
};
